import React, { useId, useState } from 'react';
import type { SyncAnalyticsDailyPoint, SyncAnalyticsSlice } from '../../api/syncTrackingApi';

export type ChartSlice = SyncAnalyticsSlice & { color: string };

/* ── Donut / pie ─────────────────────────────────────────────────────────── */

interface DonutProps {
    slices: ChartSlice[];
    size?: number;
    thickness?: number;
    centerLabel?: string;
    centerValue?: string;
    /** Keys that open a drill-down when clicked (e.g. failed / retrying). */
    clickableKeys?: string[];
    onSliceClick?: (slice: ChartSlice) => void;
}

export const DonutChart: React.FC<DonutProps> = ({
    slices,
    size = 180,
    thickness = 28,
    centerLabel,
    centerValue,
    clickableKeys,
    onSliceClick,
}) => {
    const total = slices.reduce((s, x) => s + x.value, 0);
    const r = (size - thickness) / 2;
    const c = 2 * Math.PI * r;
    const cx = size / 2;
    const cy = size / 2;
    const [hover, setHover] = useState<string | null>(null);

    let offset = 0;
    const arcs = slices.map(slice => {
        const frac = total === 0 ? 0 : slice.value / total;
        const len = frac * c;
        const arc = { ...slice, dash: len, offset, frac };
        offset += len;
        return arc;
    });

    const active = arcs.find(a => a.key === hover) ?? null;
    const canClick = (key: string) =>
        !!onSliceClick && (!clickableKeys || clickableKeys.includes(key));

    return (
        <div className="flex items-center gap-6">
            <div className="relative shrink-0" style={{ width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    <circle cx={cx} cy={cy} r={r} fill="none" stroke="#EEF1F5" strokeWidth={thickness} />
                    {arcs.map(arc => (
                        <circle
                            key={arc.key}
                            cx={cx}
                            cy={cy}
                            r={r}
                            fill="none"
                            stroke={arc.color}
                            strokeWidth={thickness}
                            strokeDasharray={`${arc.dash} ${c - arc.dash}`}
                            strokeDashoffset={-arc.offset + c / 4}
                            strokeLinecap="butt"
                            className="transition-opacity"
                            style={{
                                opacity: hover && hover !== arc.key ? 0.35 : 1,
                                cursor: canClick(arc.key) ? 'pointer' : 'default',
                            }}
                            onMouseEnter={() => setHover(arc.key)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => {
                                if (canClick(arc.key)) onSliceClick?.(arc);
                            }}
                            role={canClick(arc.key) ? 'button' : undefined}
                        />
                    ))}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    {active ? (
                        <>
                            <div className="font-mono text-[22px] font-bold tracking-[-0.5px] text-mapper-text leading-none">
                                {active.value.toLocaleString()}
                            </div>
                            <div className="text-[11.5px] font-medium text-mapper-muted mt-1">{active.label}</div>
                        </>
                    ) : (
                        <>
                            <div className="font-mono text-[22px] font-bold tracking-[-0.5px] text-mapper-text leading-none">
                                {centerValue ?? total.toLocaleString()}
                            </div>
                            {centerLabel && (
                                <div className="text-[11.5px] font-medium text-mapper-muted mt-1">{centerLabel}</div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <ul className="flex flex-col gap-2 min-w-0 flex-1">
                {slices.map(s => {
                    const pct = total === 0 ? 0 : Math.round((s.value / total) * 100);
                    const clickable = canClick(s.key);
                    return (
                        <li
                            key={s.key}
                            className={`flex items-center gap-2.5 text-[13px] rounded-lg px-2 py-1.5 transition-colors ${
                                hover === s.key ? 'bg-[#F5F7F9]' : ''
                            } ${clickable ? 'cursor-pointer' : ''}`}
                            onMouseEnter={() => setHover(s.key)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => {
                                if (clickable) onSliceClick?.(s);
                            }}
                            role={clickable ? 'button' : undefined}
                            tabIndex={clickable ? 0 : undefined}
                            onKeyDown={e => {
                                if (clickable && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    onSliceClick?.(s);
                                }
                            }}
                        >
                            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                            <span className="flex-1 text-mapper-body-text font-medium truncate">{s.label}</span>
                            <span className="font-mono text-[12.5px] font-semibold text-mapper-text">{s.value.toLocaleString()}</span>
                            <span className="font-mono text-[11.5px] text-mapper-muted-3 w-9 text-right">{pct}%</span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

/* ── Grouped daily bar chart ─────────────────────────────────────────────── */

interface BarChartProps {
    daily: SyncAnalyticsDailyPoint[];
    /** Which series pair to plot. */
    mode: 'entity' | 'direction' | 'outcome';
    height?: number;
    /** Fired when a day group is clicked (outcome mode: useful for failure drill-down). */
    onDayClick?: (day: SyncAnalyticsDailyPoint) => void;
}

type NumericSeriesKey = 'patients' | 'appointments' | 'emr_to_ghl' | 'ghl_to_emr' | 'succeeded' | 'failed';

const SERIES: Record<BarChartProps['mode'], {
    aKey: NumericSeriesKey;
    bKey: NumericSeriesKey;
    aLabel: string;
    bLabel: string;
    aColor: string;
    bColor: string;
}> = {
    entity: {
        aKey: 'patients', bKey: 'appointments',
        aLabel: 'Patients', bLabel: 'Appointments',
        aColor: '#3B5BA9', bColor: '#0E8577',
    },
    direction: {
        aKey: 'emr_to_ghl', bKey: 'ghl_to_emr',
        aLabel: 'EMR → Advital', bLabel: 'Advital → EMR',
        aColor: '#0E8577', bColor: '#6A62B5',
    },
    outcome: {
        aKey: 'succeeded', bKey: 'failed',
        aLabel: 'Synced', bLabel: 'Failed',
        aColor: '#0E8577', bColor: '#D6607A',
    },
};

export const GroupedBarChart: React.FC<BarChartProps> = ({ daily, mode, height = 220, onDayClick }) => {
    const series = SERIES[mode];
    const max = Math.max(
        1,
        ...daily.map(d => Math.max(d[series.aKey], d[series.bKey])),
    );
    const [hover, setHover] = useState<number | null>(null);

    const padL = 36;
    const padR = 8;
    const padT = 12;
    const padB = 36;
    const width = Math.max(480, daily.length * 48);
    const innerH = height - padT - padB;
    const innerW = width - padL - padR;
    const groupW = daily.length === 0 ? 0 : innerW / daily.length;
    const barW = Math.min(14, groupW * 0.32);
    const gap = 3;

    const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => Math.round(max * t));
    const clickable = !!onDayClick;

    return (
        <div className="w-full overflow-x-auto">
            <div className="flex items-center gap-4 mb-3 px-0.5">
                <LegendDot color={series.aColor} label={series.aLabel} />
                <LegendDot color={series.bColor} label={series.bLabel} />
                {hover !== null && daily[hover] && (
                    <span className="ml-auto text-[12.5px] text-mapper-muted font-medium">
                        {daily[hover].label}:{' '}
                        <span className="font-mono text-mapper-text font-semibold">
                            {daily[hover][series.aKey].toLocaleString()}
                        </span>
                        {' / '}
                        <span className="font-mono text-mapper-text font-semibold">
                            {daily[hover][series.bKey].toLocaleString()}
                        </span>
                        {clickable && (
                            <span className="text-mapper-teal-hover font-semibold"> · click for details</span>
                        )}
                    </span>
                )}
            </div>
            <svg width={width} height={height} className="block max-w-full">
                {ticks.map(t => {
                    const y = padT + innerH - (t / max) * innerH;
                    return (
                        <g key={t}>
                            <line x1={padL} x2={width - padR} y1={y} y2={y} stroke="#EEF1F5" strokeWidth={1} />
                            <text x={padL - 8} y={y + 3.5} textAnchor="end" className="fill-mapper-muted-4" style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace' }}>
                                {t}
                            </text>
                        </g>
                    );
                })}

                {daily.map((d, i) => {
                    const a = d[series.aKey];
                    const b = d[series.bKey];
                    const aH = (a / max) * innerH;
                    const bH = (b / max) * innerH;
                    const gx = padL + i * groupW + groupW / 2;
                    const dimmed = hover !== null && hover !== i;
                    const dayClickable = clickable;

                    return (
                        <g
                            key={d.date}
                            onMouseEnter={() => setHover(i)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => {
                                if (dayClickable) onDayClick?.(d);
                            }}
                            style={{
                                cursor: dayClickable ? 'pointer' : 'default',
                                opacity: dimmed ? 0.4 : 1,
                                transition: 'opacity .15s',
                            }}
                        >
                            <rect x={padL + i * groupW} y={padT} width={groupW} height={innerH + 8} fill="transparent" />
                            <rect
                                x={gx - barW - gap / 2}
                                y={padT + innerH - aH}
                                width={barW}
                                height={aH}
                                rx={3}
                                fill={series.aColor}
                            />
                            <rect
                                x={gx + gap / 2}
                                y={padT + innerH - bH}
                                width={barW}
                                height={bH}
                                rx={3}
                                fill={series.bColor}
                            />
                            <text
                                x={gx}
                                y={height - 12}
                                textAnchor="middle"
                                className="fill-mapper-muted-3"
                                style={{ fontSize: 10.5, fontWeight: 600, fontFamily: 'Public Sans, sans-serif' }}
                            >
                                {d.label.replace(/^[A-Za-z]+ /, '')}
                            </text>
                        </g>
                    );
                })}
            </svg>
        </div>
    );
};

/* ── Horizontal stacked bar (share of volume) ────────────────────────────── */

interface StackBarProps {
    slices: ChartSlice[];
}

export const StackedShareBar: React.FC<StackBarProps> = ({ slices }) => {
    const total = slices.reduce((s, x) => s + x.value, 0);
    if (total === 0) {
        return <div className="h-3 rounded-full bg-mapper-border" />;
    }
    return (
        <div className="flex flex-col gap-3">
            <div className="flex h-3 rounded-full overflow-hidden bg-mapper-border">
                {slices.map(s => (
                    <div
                        key={s.key}
                        style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
                        title={`${s.label}: ${s.value}`}
                    />
                ))}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
                {slices.map(s => (
                    <div key={s.key} className="flex items-center gap-2 text-[12.5px]">
                        <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                        <span className="text-mapper-body-text font-medium">{s.label}</span>
                        <span className="font-mono text-[12px] font-semibold text-mapper-text">
                            {Math.round((s.value / total) * 100)}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ── Sparkline for KPI tiles ─────────────────────────────────────────────── */

export const Sparkline: React.FC<{ values: number[]; color?: string }> = ({ values, color = '#0E8577' }) => {
    const id = useId();
    if (values.length < 2) return null;
    const max = Math.max(...values, 1);
    const min = Math.min(...values, 0);
    const span = Math.max(1, max - min);
    const w = 72;
    const h = 28;
    const pts = values.map((v, i) => {
        const x = (i / (values.length - 1)) * w;
        const y = h - ((v - min) / span) * (h - 4) - 2;
        return `${x},${y}`;
    }).join(' ');
    const area = `0,${h} ${pts} ${w},${h}`;

    return (
        <svg width={w} height={h} className="block">
            <defs>
                <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
            </defs>
            <polygon points={area} fill={`url(#${id})`} />
            <polyline points={pts} fill="none" stroke={color} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
};

function LegendDot({ color, label }: { color: string; label: string }) {
    return (
        <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-mapper-body-text">
            <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
            {label}
        </span>
    );
}
